const {findArguments, findOptionalArgument} = require("../util/ParserHelpers");

const PREFIXES = {
	cu: "cubic",
	sq: "square",
	fl: "fluid",
};

// Variants are later redirected to point to the same object.
const TO_WORDS = {
	ft: {
		singular: "foot",
		plural: "feet",
	},
	in: {
		singular: "inch",
		plural: "inches",
	},
	mL: {
		variants: ["ml"],
		singular: "milliliter",
		plural: "milliliters",
	},
	L: {
		variants: ["l"],
		singular: "litre",
		plural: "litres",
	},
	h: {
		variants: ["hr"],
		singular: "hour",
		plural: "hours",
	},
	km: {
		singular: "kilometer",
		plural: "kilometers",
	},
	m: {
		singular: "meter",
		plural: "meters",
	},
	cm: {
		singular: "centimeter",
		plural: "centimeters",
	},
	mm: {
		singular: "millimeter",
		plural: "millimeters",
	},
	yd: {
		singular: "yard",
		plural: "yards",
	},
	mi: {
		singular: "mile",
		plural: "miles",
	},
	min: {
		singular: "min-it",
		plural: "min-its",
	},
	s: {
		variants: ["sec", "secs"],
		singular: "second",
		plural: "seconds",
	},
	C: {
		singular: "degree celsius",
		plural: "degrees celsius",
	},
	F: {
		singular: "degree fahrenheit",
		plural: "degrees fahrenheit",
	},
	g: {
		singular: "gram",
		plural: "grams",
	},
	kg: {
		singular: "kilogram",
		plural: "kilograms",
	},
	mg: {
		singular: "milligram",
		plural: "milligrams",
	},
	ug: {
		singular: "micro-gram",
		plural: "micro-grams",
	},
	oz: {
		singular: "ounce",
		plural: "ounces",
	},
	ton: {
		singular: "ton",
		plural: "tons",
	},
	c: {
		singular: "cup",
		plural: "cups",
	},
	pt: {
		singular: "pint",
		plural: "pints",
	},
	qt: {
		singular: "quart",
		plural: "quarts",
	},
	gal: {
		singular: "gallon",
		plural: "gallons",
	},
	cent: {
		variants: ["cents"],
		singular: "cent",
		plural: "cents",
	},
	dollar: {
		variants: ["dollars"],
		singular: "dollar",
		plural: "dollars",
	},
	day: {
		variants: ["days"],
		singular: "day",
		plural: "days",
	},
	week: {
		variants: ["wk"],
		singular: "week",
		plural: "weeks",
	},
	month: {
		variants: ["mo"],
		singular: "month",
		plural: "months",
	},
	year: {
		variants: ["yr"],
		singular: "year",
		plural: "years",
	},
	decade: {
		singular: "decade",
		plural: "decades",
	},
	century: {
		singular: "century",
		plural: "centuries",
	},
	millenium: {
		singular: "millenium",
		plural: "millenia",
	},
	degree: {
		variants: ["degrees", "deg", "degs", "°"],
		singular: "degree",
		plural: "degrees",
	},
	radian: {
		variants: ["rad", "radians"],
		singular: "radian",
		plural: "radians",
	},
	pound: {
		variants: ["lb", "lbs"],
		singular: "pound",
		plural: "pounds",
	},
};
for (const key of Object.keys(TO_WORDS)) {
	for (const variant of TO_WORDS[key].variants || []) {
		TO_WORDS[variant] = TO_WORDS[key];
	}
}
Object.freeze(TO_WORDS);
const GUESSABLE_PREFIXES = Object.freeze(Object.keys(PREFIXES));
const GUESSABLE_UNITS = Object.freeze(Object.keys(TO_WORDS));

/**
 * Split a unit designation into its semantic parts.
 *
 * ex.
 * 	partsOf('sq. mi.') => ['sq', 'mi']
 * 	partsOf('sq mi')   => ['sq', 'mi']
 * 	partsOf('mi')      => ['mi']
 */
const partsOf = function(unit) {
	return unit
		.replace(/[.]/g, "")
		.split(" ")
		.filter(part => part.length);
};

const unitsParser = function(ps) {
	// Parse for the unit node here. Figure out what the unit is, and what to read it as.

	let unit;
	let plural = true;
	let readAs = "";

	let needsReadAs = false;
	let hasReadAs = false; // whether readAs arg given

	const optArg = findOptionalArgument(ps);
	const extraOptArg = findOptionalArgument(ps);

	const arg1 = findArguments(ps, 1);

	/* Required arg parsing. */

	if (!arg1 || !arg1.length) {
		ps.pushError(
			`\\units must be provided unit to use. Ex. \\units{sq m} (square meters) or \\units{mi} (miles).`
		);
	} else {
		unit = arg1[0].text.trim();

		// require readAs if unguessable
		const prefixes = partsOf(unit); // still has base unit, which we pop off
		const baseUnit = prefixes.pop();

		needsReadAs =
			prefixes.length > 1 || // too many parts (we only do one part, ex. the sq in sq mi, for now)
			!prefixes.every(p => GUESSABLE_PREFIXES.includes(p)) || // one part that isn't guessable
			!GUESSABLE_UNITS.includes(baseUnit); // unit isn't guessable
	}

	if (extraOptArg) {
		ps.pushError(`\\units given too many optional arguments.`);
	}

	/* Optional arg parsing. */
	if (optArg) {
		const value = optArg.getRest();
		switch (value) {
			case "1":
				plural = false;
				break;
			case "+":
				plural = true;
				break;
			default:
				hasReadAs = true;
				readAs = value;
				break;
		}
	}

	/* guess readAs if necessary */
	if (!hasReadAs) {
		/* error if readAs not given for an unknown word */

		if (needsReadAs) {
			ps.pushError(
				`\\units given no read value with unknown unit ${unit}. Provide a [read as] argument ex. \\units[zetabytes]{${unit}} or use one of the following: ${GUESSABLE_UNITS.join(
					", "
				)}.`
			);
		} else {
			const parts = partsOf(unit);

			/* convert parts to their read counterparts and set readAs to that */

			// has prefix
			if (parts.length >= 2) {
				parts[0] = PREFIXES[parts[0]];
			}

			// replace the last (unit) part with the actual unit
			parts.push(TO_WORDS[parts.pop()][plural ? "plural" : "singular"]);

			readAs = parts.join(" ");
		}
	}

	return [
		{
			type: "units",
			children: [],
			unit,
			readAs,
			text: unit,
			unitText: readAs, // backwards compatibility
		},
	];
};

// node
const unitsNode = {
	draw(rc, x, y, node, measure) {
		return rc.drawNode(rc, x, y, {type: "text", text: node.unit}, measure);
	},
	measure(rc, fontInfo, props, node) {
		// return the measure of the text node
		return rc.measureNode(rc, fontInfo, props, {type: "text", text: node.unit});
	},
	// read aloud the unit with the right plurality
	readAloud: node => {
		return '"' + node.readAs + '"';
	},
};

module.exports = {
	parsers: {
		nonmathCommands: {
			units: unitsParser,
			unit: unitsParser,
		},
		mathCommands: {
			units: unitsParser,
			unit: unitsParser,
		},
	},
	nodes: {
		units: unitsNode,
		unit: unitsNode,
	},
};
